import type { ReactNode } from 'react';
import clsx from 'clsx';
import { SIDE_PANEL_MARKER } from '@/shared/lib/chat-layout';
import { PanelReveal } from '@/shared/ui/PanelReveal';

export interface SessionContentLayoutProps {
  appSidebar: ReactNode;
  leftPanel: ReactNode;
  leftPanelTestId?: string;
  /**
   * Sizes the panel's content, measured against the session row (an `@container`) so it
   * holds its open size while the panel slides open and closed around it.
   */
  leftPanelContentClassName: string;
  isLeftPanelOpen: boolean;
  /** A fullscreen panel hides the chat, so it keeps no gutter beside it. */
  isLeftPanelFullscreen: boolean;
  /** Keeps a closed panel's content mounted (and inert), such as a hidden widget's inner view. */
  renderLeftPanel: boolean;
  header: ReactNode;
  chatAreaClassName?: string;
  children: ReactNode;
}

export function SessionContentLayout({
  appSidebar,
  leftPanel,
  leftPanelTestId,
  leftPanelContentClassName,
  isLeftPanelOpen,
  isLeftPanelFullscreen,
  renderLeftPanel,
  header,
  chatAreaClassName,
  children,
}: SessionContentLayoutProps) {
  return (
    <div className="flex h-dvh">
      <aside className="z-10 shrink-0">{appSidebar}</aside>
      <div className="relative min-w-0 flex-1 bg-theme-background h-dvh">
        <main className="h-full">
          <div data-testid="session-row" className="@container flex h-full p-app-inset">
            {/* The chat takes whatever the panel leaves, so it follows the panel's animation
                rather than running one of its own that would have to stay in step. */}
            <div
              data-testid="chat-content-area"
              className={clsx('h-full min-w-0 flex-1', chatAreaClassName)}
            >
              {/* Header and content are separate surfaces, as on the sessions list:
                  nesting them in one card leaves no gap for the frame to show. */}
              <div className="flex h-full min-h-0 flex-col gap-app-inset">
                {header}
                <div className="min-h-0 flex-1 overflow-hidden rounded-lg bg-background">
                  {children}
                </div>
              </div>
            </div>
            {/* Measured by widgets for the width a chat column and an open panel have; it follows
                the reveal, so a closed panel measures 0. */}
            <div {...SIDE_PANEL_MARKER} className="flex h-full shrink-0">
              <PanelReveal
                isOpen={isLeftPanelOpen}
                keepMounted={renderLeftPanel}
                axis="width"
                gapEdge={isLeftPanelFullscreen ? undefined : 'left'}
                fadeOnClose
                className="h-full shrink-0"
              >
                <div
                  data-testid={leftPanelTestId}
                  className={clsx('h-full', leftPanelContentClassName)}
                >
                  {leftPanel}
                </div>
              </PanelReveal>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
